import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { AutoColumn } from '../../components/Column'
import { TYPE } from '../../theme'
import {
  asBillionsKishu,
  burnSupplyAsKishu,
  fetchKishuStats,
  formatPrecise,
  formatTokenAmount,
  formatUsd,
  KishuStatsPayload,
  pricePerBillionKishu
} from '../../utils/kishuStats'

type Row = {
  label: string
  field?: string
  value: string
}

const PageWrapper = styled(AutoColumn)`
  max-width: 820px;
  width: 100%;
`

const StatsBody = styled.div`
  width: 100%;
  padding: 24px;
  background: ${({ theme }) => theme.bg1};
  box-shadow: 0 24px 32px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.08);
  border-radius: 30px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    align-items: flex-start;
    flex-direction: column;
  `};
`

const RefreshButton = styled.button`
  border: 0;
  border-radius: 18px;
  padding: 10px 18px;
  color: white;
  background: radial-gradient(174.47% 188.91% at 1.84% 0%, #0cccfc 0%, #021d43 100%);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  :disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    grid-template-columns: 1fr;
  `};
`

const StatCell = styled.div`
  min-width: 0;
  padding: 14px 16px;
  border-radius: 18px;
  background: ${({ theme }) => theme.bg2};
`

const Label = styled.div`
  color: ${({ theme }) => theme.text2};
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
`

const FieldName = styled.div`
  color: ${({ theme }) => theme.text3};
  font-family: monospace;
  font-size: 12px;
  margin-bottom: 6px;
`

const Value = styled.div`
  color: ${({ theme }) => theme.text1};
  font-size: 18px;
  font-weight: 600;
  overflow-wrap: anywhere;
`

const StatusText = styled.div`
  color: ${({ theme }) => theme.text2};
  font-size: 14px;
`

const ErrorText = styled(StatusText)`
  color: ${({ theme }) => theme.red1};
`

function rowsFromStats(stats: KishuStatsPayload): Row[] {
  const burnedKishu = burnSupplyAsKishu(stats)

  return [
    { label: 'Timestamp', field: 'timestamp', value: stats.timestamp },
    { label: 'KISHU Price USD', field: 'kishuPriceUsdt', value: formatPrecise(stats.kishuPriceUsdt) },
    { label: '1 Billion KISHU Price', field: 'derived', value: formatUsd(pricePerBillionKishu(stats), 4) },
    { label: 'KISHU Price ETH', field: 'kishuPriceEth', value: formatPrecise(stats.kishuPriceEth) },
    { label: 'Max Supply (KISHU)', field: 'maxSupply', value: formatTokenAmount(stats.maxSupply, 0) },
    { label: 'Circulating Supply (KISHU)', field: 'circulatingSupply', value: formatTokenAmount(stats.circulatingSupply, 2) },
    { label: 'Total Supply (KISHU)', field: 'totalSupply', value: formatTokenAmount(stats.totalSupply, 2) },
    { label: 'Burned (KISHU)', field: 'burnSupply', value: formatTokenAmount(burnedKishu, 2) },
    { label: 'Burned (Billions KISHU)', field: 'derived', value: `${formatTokenAmount(asBillionsKishu(burnedKishu), 2)} B` },
    { label: 'Holders', field: 'holders', value: formatTokenAmount(stats.holders, 0) },
    { label: 'Daily Transfers', field: 'dailyTransfers', value: formatTokenAmount(stats.dailyTransfers, 0) },
    { label: 'Daily Volume (KISHU)', field: 'dailyVolume', value: formatTokenAmount(stats.dailyVolume, 2) },
    { label: 'Weekly Transfers', field: 'weeklyTransfers', value: formatTokenAmount(stats.weeklyTransfers, 0) },
    { label: 'Weekly Volume (KISHU)', field: 'weeklyVolume', value: formatTokenAmount(stats.weeklyVolume, 2) },
    { label: 'Monthly Transfers', field: 'monthlyTransfers', value: formatTokenAmount(stats.monthlyTransfers, 0) },
    { label: 'Monthly Volume (KISHU)', field: 'monthlyVolume', value: formatTokenAmount(stats.monthlyVolume, 2) },
    { label: 'Total Transfers', field: 'totalTransfers', value: formatTokenAmount(stats.totalTransfers, 0) },
    { label: 'Total Volume (KISHU)', field: 'totalVolume', value: formatTokenAmount(stats.totalVolume, 2) }
  ]
}

export default function Stats() {
  const [stats, setStats] = useState<KishuStatsPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadStats = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      setStats(await fetchKishuStats())
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Stats request failed')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  return (
    <PageWrapper>
      <StatsBody>
        <Toolbar>
          <AutoColumn gap="4px">
            <TYPE.mediumHeader>Stats Feed</TYPE.mediumHeader>
            <StatusText>{loading ? 'Loading current Worker output...' : 'Current cached Worker output'}</StatusText>
          </AutoColumn>
          <RefreshButton onClick={loadStats} disabled={loading}>
            Refresh
          </RefreshButton>
        </Toolbar>

        {error && <ErrorText>{error}</ErrorText>}

        {stats && (
          <Grid>
            {rowsFromStats(stats).map(row => (
              <StatCell key={row.label}>
                <Label>{row.label}</Label>
                {row.field && <FieldName>{row.field}</FieldName>}
                <Value>{row.value}</Value>
              </StatCell>
            ))}
          </Grid>
        )}
      </StatsBody>
    </PageWrapper>
  )
}
